import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from "@angular/core";
import { FormControl, NonNullableFormBuilder, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, Observable, Subscription, timer } from "rxjs";
import { debounceTime, map, startWith, take } from "rxjs/operators";
import { SnackbarService } from "../snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { IApiError } from "../../../../../common/services/api/onsip-api-action";
import { MusicSource } from "@onsip/common/services/api/resources/musicSource/music-source";
import { MusicSourceService } from "@onsip/common/services/api/resources/musicSource/music-source.service";
import { MusicStreamSource } from "@onsip/common/services/api/resources/musicStreamSource/music-stream-source";
import { MusicStreamSourceService } from "@onsip/common/services/api/resources/musicStreamSource/music-stream-source.service";

interface MusicStreamSourceWithGenres extends Partial<MusicStreamSource> {
  isIndeterminate?: boolean;
  isChecked?: boolean;
  sources?: Array<MusicStreamSource>;
  genre: string;
}

enum AudioSourceCheckStates {
  CHECKING = "CHECKING",
  INVALID = "INVALID",
  VALID = "VALID"
}

@Component({
  selector: "onsip-edit-music-source-dialog",
  styleUrls: ["./edit-music-source-dialog.component.scss"],
  templateUrl: "./edit-music-source-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class EditMusicSourceDialogComponent implements OnInit, OnDestroy {
  readonly BACKEND_ERROR = "BACKEND_ERROR";
  audioSourceCheckStates = AudioSourceCheckStates;

  editSourceForm = this.fb.group({
    name: this.fb.control("", [Validators.required]),
    url: this.fb.control("", [Validators.required]),
    audioSourceCheckState: this.fb.control("", [Validators.pattern(AudioSourceCheckStates.VALID)])
  });
  searchControl = new FormControl("", { nonNullable: true });

  audioSourceUrl = new BehaviorSubject("");
  isSavingMusicSource = new BehaviorSubject(false);
  sources = new BehaviorSubject<Array<MusicStreamSourceWithGenres>>([]);
  displayedSources: Observable<Array<MusicStreamSourceWithGenres>> = this.initDisplayedSource();
  isSourcesListLoading = this.musicStreamSource.state.pipe(map(({ loading }) => loading));
  isCustomSourceLink = false;

  private unsubscriber = new Subscription();

  constructor(
    private fb: NonNullableFormBuilder,
    private dialogRef: MatDialogRef<EditMusicSourceDialogComponent>,
    private musicSourseService: MusicSourceService,
    private musicStreamSource: MusicStreamSourceService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: MusicSource,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.unsubscriber.add(
      this.musicStreamSource
        .browseIfNoData()
        .pipe(take(1))
        .subscribe(({ state }) => this.sources.next(this.groupSourcesByGenre(Object.values(state))))
    );

    this.unsubscriber.add(
      this.editSourceForm.controls["url"].valueChanges.pipe(debounceTime(200)).subscribe(url => {
        this.audioSourceUrl.next(url);
        this.editSourceForm
          .get("audioSourceCheckState")
          ?.patchValue(this.audioSourceCheckStates.CHECKING);
        const source = this.findSourceByUrl(this.sources.value, url);
        this.resetCheckState();
        if (source) {
          this.checkSource(source);
          this.isCustomSourceLink = false;
        } else {
          this.isCustomSourceLink = true;
          this.editSourceForm.controls.audioSourceCheckState.patchValue(
            this.audioSourceCheckStates.VALID
          );
        }
      })
    );

    if (this.data) {
      this.editSourceForm.patchValue({
        name: this.data.name,
        url: this.data.url
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  onSubmit(): void {
    if (this.editSourceForm.valid && !this.isSavingMusicSource.value) {
      this.isSavingMusicSource.next(true);
      (this.data
        ? this.musicSourseService.musicSourceEdit({
            musicSourceId: this.data.musicSourceId,
            name: this.editSourceForm.controls.name.value,
            url: this.editSourceForm.controls.url.value
          })
        : this.musicSourseService.musicSourceAdd({
            name: this.editSourceForm.controls.name.value,
            url: this.editSourceForm.controls.url.value
          })
      ).then(response => {
        if (response.status === "error") {
          this.handleErrorOnAdding(response.data);
        } else {
          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_ADDED", {
              value: this.editSourceForm.value.name
            }),
            "success"
          );
          this.dialogRef.close();
        }
      });
    }
  }

  changeAudioSourceCheckState(state: AudioSourceCheckStates): void {
    this.editSourceForm.get("audioSourceCheckState")?.patchValue(state);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  trackByFn(i: number, source: MusicStreamSourceWithGenres): string {
    return `${source.url}-${source.genre}-${source.isChecked}-${source.isIndeterminate}`;
  }

  setCurrentMusicSource(musicSource: MusicStreamSourceWithGenres): void {
    this.editSourceForm.patchValue({
      name: musicSource.name,
      url: musicSource.url
    });
  }

  private findSourceByUrl(
    sources: Array<MusicStreamSourceWithGenres>,
    url: string
  ): MusicStreamSourceWithGenres | undefined {
    return sources.find(source => source.url === url);
  }

  private resetCheckState(): void {
    this.sources.next(
      this.sources.value.map(groupedGenre => ({
        ...groupedGenre,
        isIndeterminate: false,
        isChecked: false
      }))
    );
  }

  private checkSource(musicSource: MusicStreamSourceWithGenres): void {
    // Duck tape. For some reason angular incorrectly cheks checkbox inside virtual scrolling
    this.unsubscriber.add(
      timer(50).subscribe(() => {
        this.sources.next(
          this.sources.value.map(groupedGenre => {
            if (musicSource.genre === groupedGenre.genre) {
              const genreSourceCount = groupedGenre.sources?.length || 0;
              return {
                ...groupedGenre,
                isIndeterminate: genreSourceCount > 1,
                isChecked: genreSourceCount === 1 || musicSource.url === groupedGenre.url
              };
            } else {
              return {
                ...groupedGenre,
                isIndeterminate: false,
                isChecked: false
              };
            }
          })
        );
      })
    );
  }

  private groupSourcesByGenre(
    sources: Array<MusicStreamSource>
  ): Array<MusicStreamSourceWithGenres> {
    const groupedSourcesRecord: Record<string, MusicStreamSourceWithGenres> = {};

    sources.forEach(musicSource => {
      const { genre } = musicSource;
      if (groupedSourcesRecord[genre]) {
        groupedSourcesRecord[genre].sources?.push(musicSource);
      } else {
        groupedSourcesRecord[genre] = {
          genre,
          isIndeterminate: false,
          isChecked: false,
          sources: [musicSource]
        };
      }
    });

    /* List should be flat to use virtual scrolling properly */
    let result: Array<MusicStreamSourceWithGenres> = [];

    Object.keys(groupedSourcesRecord).forEach(genre => {
      groupedSourcesRecord[genre]?.sources?.sort();
      result.push(groupedSourcesRecord[genre]);
      result = [...result, ...(groupedSourcesRecord[genre].sources || [])];
    });

    return result;
  }

  private handleErrorOnAdding(error: IApiError): void {
    if (error.parameter === "Url") {
      this.editSourceForm.get("url")?.setErrors({ [this.BACKEND_ERROR]: error.message });
    }

    if (error.parameter === "Name") {
      this.editSourceForm.get("name")?.setErrors({ [this.BACKEND_ERROR]: error.message });
    }

    this.cdr.markForCheck();
  }

  private doesFit(source: MusicStreamSourceWithGenres, search: string): boolean {
    search = search.trim().toLowerCase();

    if (!search) {
      return true;
    }

    return (
      source.genre.toLowerCase().includes(search) || !!source?.name?.toLowerCase()?.includes(search)
    );
  }

  private initDisplayedSource(): Observable<Array<MusicStreamSourceWithGenres>> {
    return combineLatest([
      this.sources,
      this.searchControl.valueChanges.pipe(startWith(""), debounceTime(300))
    ]).pipe(
      map(([sources, search]: [Array<MusicStreamSourceWithGenres>, string]) => {
        if (!search) {
          return sources;
        }

        const presentedGenres: Record<string, boolean> = {};
        const result: Array<MusicStreamSourceWithGenres> = [];

        // need to go from other end to go through sources just one time because list is very long
        for (let i = sources.length - 1; i > -1; --i) {
          if (sources[i].sources) {
            if (presentedGenres[sources[i].genre]) {
              result.push(sources[i]);
            }

            continue;
          }

          if (this.doesFit(sources[i], search)) {
            presentedGenres[sources[i].genre] = true;
            result.push(sources[i]);
          }
        }

        result.reverse();

        return result;
      })
    );
  }
}
